export function formatDateTime(timestamp?: number) {
  if (!timestamp) {
    return "n/a";
  }

  return new Date(timestamp).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatRelativeTime(timestamp?: number, now = Date.now()) {
  if (!timestamp) {
    return "-";
  }

  const diffMs = timestamp - now;
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs < hour) {
    return rtf.format(Math.round(diffMs / minute), "minute");
  }
  if (abs < day) {
    return rtf.format(Math.round(diffMs / hour), "hour");
  }
  return rtf.format(Math.round(diffMs / day), "day");
}

const ISO_CURRENCY_CODE = /^[A-Z]{3}$/u;
const COMPACT_SUFFIXES: Readonly<Record<string, string>> = {
  K: "k",
  M: "m",
  B: "bn",
};

function normalizeCompactSuffix(value: string): string {
  return value.replace(/([KMB])$/u, (suffix) => COMPACT_SUFFIXES[suffix] ?? suffix);
}

/*
 * One compact-number formatter for the app: Intl-backed so currency renders
 * with its real symbol (en-GB compact suffixes: k / m / bn). Non-ISO
 * "currency" strings (legacy symbol configs) fall back to a plain prefix.
 */
export function formatCompactValue(
  value: number,
  options: { currency?: string | null } = {},
): string {
  if (!Number.isFinite(value)) {
    return "-";
  }

  const currency = options.currency ?? null;
  const isoCurrency = currency && ISO_CURRENCY_CODE.test(currency) ? currency : null;
  const formatted = normalizeCompactSuffix(new Intl.NumberFormat("en-GB", {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    ...(isoCurrency
      ? { style: "currency" as const, currency: isoCurrency, currencyDisplay: "narrowSymbol" as const }
      : {}),
  }).format(value));
  return currency && !isoCurrency ? `${currency} ${formatted}` : formatted;
}
